import { CommonModule, formatNumber } from '@angular/common';
import { Component, Inject, Input, LOCALE_ID, OnChanges, SimpleChanges } from '@angular/core';
import { ApexAxisChartSeries, ApexChart, ApexDataLabels, ApexGrid, ApexLegend, ApexPlotOptions, ApexTooltip, ApexXAxis, ApexYAxis, NgApexchartsModule } from 'ng-apexcharts';
import { ChartColorsConstants } from 'src/app/constants/chart-colors-constants';

@Component({
  selector: 'app-custom-bar-chart',
  templateUrl: './custom-bar-chart.component.html',
  styleUrls: ['./custom-bar-chart.component.scss'],
  standalone: true,
  imports: [CommonModule, NgApexchartsModule]
})
export class CustomBarChartComponent implements OnChanges {

  @Input() labels: string[] = [];
  @Input() values: number[] = [];
  @Input() colors: string[] = [];
  @Input() seriesName: string = 'Cantidad';
  @Input() horizontal: boolean = true;
  @Input() distributed: boolean = true;
  @Input() height: number = 300;

  chart: ApexChart = { type: 'bar' };
  chartColors: string[] = ChartColorsConstants.defaultColors;
  dataLabels: ApexDataLabels = {};
  grid: ApexGrid = {};
  hasData: boolean = false;
  legend: ApexLegend = { show: false };
  plotOptions: ApexPlotOptions = {};
  series: ApexAxisChartSeries = [];
  tooltip: ApexTooltip = {};
  states: ApexStates = { active: { filter: { type: 'none' } } };
  xaxis: ApexXAxis = {};
  yaxis: ApexYAxis = {};

  constructor(@Inject(LOCALE_ID) private locale: string) { }

  ngOnChanges(changes: SimpleChanges) {
    const unchanged = Object.values(changes).every(change =>
      !change.firstChange && JSON.stringify(change.previousValue) === JSON.stringify(change.currentValue)
    );
    if (unchanged) return;

    const values = this.values ?? [];
    const labels = this.labels ?? [];
    const scale = this.getValueAxisScale(Math.max(...values, 0));
    const formatter = (value: number | string) => this.formatValue(value);

    this.series = [{
      name: this.seriesName,
      data: [...values]
    }];
    this.hasData = values.some(value => value > 0);
    this.chartColors = this.colors?.length ? this.colors : ChartColorsConstants.defaultColors;
    this.chart = {
      type: 'bar',
      height: this.height,
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      },
      animations: {
        enabled: true
      }
    };
    this.plotOptions = {
      bar: {
        horizontal: this.horizontal,
        distributed: this.distributed,
        borderRadius: 4,
        borderRadiusApplication: 'end',
        barHeight: '55%',
        columnWidth: '55%',
        dataLabels: { position: 'top' }
      }
    };
    this.dataLabels = {
      enabled: true,
      offsetX: this.horizontal ? 28 : 0,
      offsetY: this.horizontal ? 0 : -20,
      style: { fontSize: '12px', fontWeight: 600, colors: ['#334155'] },
      formatter
    };
    this.grid = {
      borderColor: '#e2e8f0',
      xaxis: {
        lines: {
          show: this.horizontal
        }
      },
      yaxis: {
        lines: {
          show: !this.horizontal
        }
      }
    };
    this.xaxis = this.horizontal
      ? { categories: labels, ...scale, labels: { formatter } }
      : { categories: labels, labels: { style: { fontSize: '12px' } } };
    this.yaxis = this.horizontal
      ? { labels: { style: { fontSize: '12px' } } }
      : { ...scale, labels: { formatter } };
    this.tooltip = { y: { formatter } };
  }

  private formatValue(value: number | string): string {
    return formatNumber(Number(value), this.locale, '1.0-0');
  }

  private getValueAxisScale(max: number): { min: number; max: number; tickAmount: number } {
    const target = Math.max(max * 1.15, max + 1);
    const rawStep = target / 5;
    const magnitude = 10 ** Math.floor(Math.log10(rawStep));
    const niceFactor = [1, 2, 5, 10].find(factor => factor * magnitude >= rawStep) ?? 10;
    const step = Math.max(1, niceFactor * magnitude);
    const axisMax = Math.ceil(target / step) * step;

    return { min: 0, max: axisMax, tickAmount: Math.round(axisMax / step) };
  }
}
